/**
 * Utility functions to more easily interpret user settings, relationships, and access permissions between objects
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import { FOLLOWS } from 'client/shared/js/enums/relationships/follows'

/**
 * Validates whether the two provided objects are the same user id
 * @param {Object} user The user domain model
 * @param {Object} owner The owner domain model
 */
export const isSameUser = (user = {}, owner = {}) => {
  return (user.uid === owner.uid)
}

/**
 * Returns the user if the current user is a following of the provided follow user id
 * @param {Object} user The user domain model
 * @param {String} followUid The user id of the potential user to follow
 */
export const getUserFollowing = (user = {}, followUid) => {
  const { follows = {} } = user
  const following = follows.following || {}
  const userFollowing = following[followUid]

  return userFollowing
}

/**
 * Returns the user if the provided follow user id is a follower of the current user
 * @param {Object} user The user domain model
 * @param {String} followUid The user id of the potential user follower
 */
export const getUserFollower = (user = {}, followUid) => {
  const { follows = {} } = user
  const followers = follows.followers || {}
  const userFollower = followers[followUid]

  return userFollower
}

/**
 * Returns the boolean that indicates whether the current user is a following the provided follow user id
 *  with the provided relationship type
 * @param {Object} user The user domain model
 * @param {String} followUid The user id of the potential user to follow
 * @param {String} type The follow relationship type to check against
 */
export const isFollowingUser = (user = {}, followUid, type = FOLLOWS) => {
  const userFollowing = getUserFollowing(user, followUid) || {}

  return (userFollowing.type === type)
}

/**
 * Returns whether user is private
 * @param {Object} user The user domain model
 */
export const isPrivateUser = (user = {}) => {
  return (user.settings && user.settings.account) ? !!user.settings.account.isPrivate : false
}
